import { computed, ref } from "vue"
import { defineStore } from "pinia"

import { ApiError, apiRequest } from "../lib/api"
import type { MeResponse, WorkspaceSummary } from "../lib/types"

interface EmailStartResponse {
  verification_id: string
  expires_at: string
  delivery: "development" | "email"
  development_code: string | null
}

export const useSessionStore = defineStore("session", () => {
  const me = ref<MeResponse | null>(null)
  const initialized = ref(false)
  const loading = ref(false)
  const bootstrapError = ref("")

  const workspace = computed<WorkspaceSummary | null>(() => me.value?.workspaces[0] ?? null)
  const csrfToken = computed(() => me.value?.csrf_token ?? "")
  const authenticated = computed(() => me.value !== null)
  const isSystemAdmin = computed(() => me.value?.is_system_admin ?? false)

  async function bootstrap(): Promise<void> {
    if (initialized.value) return
    bootstrapError.value = ""
    try {
      me.value = await apiRequest<MeResponse>("/v1/me")
    } catch (error) {
      me.value = null
      if (!(error instanceof ApiError) || error.status !== 401) {
        bootstrapError.value = "API 服务暂时不可用，登录操作需要先启动后端。"
      }
    } finally {
      initialized.value = true
    }
  }

  async function refresh(): Promise<void> {
    me.value = await apiRequest<MeResponse>("/v1/me")
    initialized.value = true
  }

  async function startEmailSignIn(email: string): Promise<EmailStartResponse> {
    loading.value = true
    try {
      return await apiRequest<EmailStartResponse>("/v1/auth/email/start", {
        method: "POST",
        body: { email },
      })
    } finally {
      loading.value = false
    }
  }

  async function verifyEmail(verificationId: string, code: string): Promise<void> {
    loading.value = true
    try {
      await apiRequest("/v1/auth/email/verify", {
        method: "POST",
        body: { verification_id: verificationId, code },
      })
      await refresh()
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    await apiRequest("/v1/auth/logout", {
      method: "POST",
      csrfToken: csrfToken.value,
    })
    me.value = null
  }

  return {
    me,
    initialized,
    loading,
    bootstrapError,
    workspace,
    csrfToken,
    authenticated,
    isSystemAdmin,
    bootstrap,
    refresh,
    startEmailSignIn,
    verifyEmail,
    logout,
  }
})
